import { IconHistory, IconSettings } from "@tabler/icons-react";
import cn from "classnames";
import { Transition } from "components/shared/column";
import { Select } from "components/shared/form";
import Loader from "components/shared/loader";
import { formatDate } from "helpers/date";
import { paths } from "helpers/paths";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";
import {
    applyCarsPerformancePlan,
    applyLogistsPerformancePlan,
    applyOrganizationPerformancePlan,
    getCarsWithoutPerformancePlan,
    getLogistsWithoutPerformancePlan,
    getPerformancePlan,
    getPerformancePlanChanges,
    getPerformancePlanExecution,
} from "sources/performancePlans";
import PerformancePlanExecution from "./execution";
import PerformancePlanHistory from "./history";
import MonthPicker from "./monthPicker";
import PerformancePlanSettings from "./settings";
import {
    CarWithoutPerformancePlanModel,
    EditablePerformancePlan,
    LogistWithoutPerformancePlanModel,
    OrganizationPerformancePlanModel,
    PerformancePlanChangeLogModel,
    PerformancePlanExecutionModel,
    PerformancePlanFilter,
    PerformancePlanSubjectModel,
} from "./types";

const filterOptions = [
    { value: "withPlan", label: "С планом" },
    { value: "withoutPlan", label: "Без плана" },
];

type PerformancePlanFilterSelectProps = {
    inputProps: { name: string };
    options: typeof filterOptions;
    value: PerformancePlanFilter;
    onChange: (data: { value: PerformancePlanFilter }) => void;
    unClearable: boolean;
    unSearchable: boolean;
};

const PerformancePlanFilterSelect = Select as unknown as React.ComponentType<PerformancePlanFilterSelectProps>;

const hasPlan = (item: PerformancePlanSubjectModel) => item.mileagePlan !== null || item.overallBidsPricePlan !== null;

const normalizeSubject = (item: PerformancePlanSubjectModel): PerformancePlanSubjectModel => ({
    subjectId: item.subjectId,
    displayName: item.displayName,
    mileagePlan: item.mileagePlan ?? null,
    overallBidsPricePlan: item.overallBidsPricePlan ?? null,
});

const addMissingSubjects = (
    plannedSubjects: PerformancePlanSubjectModel[] = [],
    missingSubjects: PerformancePlanSubjectModel[],
) => {
    const plannedIds = new Set(plannedSubjects.map((item) => item.subjectId));
    const seen = new Set(plannedIds);
    const uniqueMissing = missingSubjects.filter((item) => {
        if (!item.subjectId || seen.has(item.subjectId)) {
            return false;
        }
        seen.add(item.subjectId);
        return true;
    });
    return plannedSubjects.map(normalizeSubject).concat(uniqueMissing);
};

const logistWithoutPlanToSubject = (item: LogistWithoutPerformancePlanModel): PerformancePlanSubjectModel => ({
    subjectId: item.id,
    displayName: item.displayName,
    mileagePlan: null,
    overallBidsPricePlan: null,
});

const carWithoutPlanToSubject = (item: CarWithoutPerformancePlanModel): PerformancePlanSubjectModel => ({
    subjectId: item.id,
    displayName: [item.number, item.brandType].filter(Boolean).join(" "),
    mileagePlan: null,
    overallBidsPricePlan: null,
});

const createDraft = (
    month: string,
    plan: OrganizationPerformancePlanModel,
    logistsWithoutPlan: LogistWithoutPerformancePlanModel[],
    carsWithoutPlan: CarWithoutPerformancePlanModel[],
): EditablePerformancePlan => ({
    month,
    autoCopyToNextMonth: !!plan.autoCopyToNextMonth,
    mileagePlan: plan.mileagePlan ?? null,
    overallBidsPricePlan: plan.overallBidsPricePlan ?? null,
    logistsPlans: addMissingSubjects(plan.logistsPlans, logistsWithoutPlan.map(logistWithoutPlanToSubject)),
    carsPlans: addMissingSubjects(plan.carsPlans, carsWithoutPlan.map(carWithoutPlanToSubject)),
});

const preparePlanForSave = (
    draft: EditablePerformancePlan,
    loadedPlan: OrganizationPerformancePlanModel,
): EditablePerformancePlan => {
    const existingLogistIds = new Set((loadedPlan.logistsPlans || []).map((item) => item.subjectId));
    const existingCarIds = new Set((loadedPlan.carsPlans || []).map((item) => item.subjectId));
    return {
        ...draft,
        logistsPlans: draft.logistsPlans.filter((item) => existingLogistIds.has(item.subjectId) || hasPlan(item)),
        carsPlans: draft.carsPlans.filter((item) => existingCarIds.has(item.subjectId) || hasPlan(item)),
    };
};

const errorMessage = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback);

export default function PerformancePlans({ history, location }: RouteComponentProps) {
    const [month, setMonth] = useState(() => formatDate(new Date())?.slice(0, 7) || "");
    const monthRef = useRef(month);
    monthRef.current = month;
    const [filter, setFilter] = useState<PerformancePlanFilter>("withPlan");

    const isHistory = location.pathname === paths.performancePlansHistory;
    const isSettings = location.pathname === paths.performancePlansSettings;
    const isExecution = !isHistory && !isSettings;

    const [execution, setExecution] = useState<PerformancePlanExecutionModel | null>(null);
    const [isExecutionLoading, setIsExecutionLoading] = useState(false);
    const [executionError, setExecutionError] = useState<string | null>(null);
    const [loadedExecutionMonth, setLoadedExecutionMonth] = useState<string | null>(null);

    const [plan, setPlan] = useState<OrganizationPerformancePlanModel | null>(null);
    const [carsWithoutPlan, setCarsWithoutPlan] = useState<CarWithoutPerformancePlanModel[]>([]);
    const [logistsWithoutPlan, setLogistsWithoutPlan] = useState<LogistWithoutPerformancePlanModel[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [loadedPlanMonth, setLoadedPlanMonth] = useState<string | null>(null);
    const [draft, setDraft] = useState<EditablePerformancePlan | null>(null);
    const [initialDraft, setInitialDraft] = useState<EditablePerformancePlan | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const [changes, setChanges] = useState<PerformancePlanChangeLogModel[]>([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState<string | null>(null);
    const [loadedHistoryMonth, setLoadedHistoryMonth] = useState<string | null>(null);

    const loadPlan = useCallback((targetMonth: string) => {
        setIsLoading(true);
        setLoadError(null);

        Promise.all([
            getPerformancePlan(targetMonth),
            getCarsWithoutPerformancePlan(targetMonth),
            getLogistsWithoutPerformancePlan(targetMonth),
        ])
            .then(([loadedPlan, cars, logists]) => {
                if (monthRef.current !== targetMonth) {
                    return;
                }
                if (!loadedPlan || !Array.isArray(cars) || !Array.isArray(logists)) {
                    throw new Error("Не удалось загрузить данные. Попробуйте еще раз.");
                }
                setPlan(loadedPlan);
                setCarsWithoutPlan(cars);
                setLogistsWithoutPlan(logists);
                setLoadedPlanMonth(targetMonth);
                setIsLoading(false);
            })
            .catch((error: unknown) => {
                if (monthRef.current !== targetMonth) {
                    return;
                }
                setLoadError(errorMessage(error, "Не удалось загрузить данные. Попробуйте еще раз."));
                setIsLoading(false);
            });
    }, []);

    const loadHistory = useCallback((targetMonth: string) => {
        setIsHistoryLoading(true);
        setHistoryError(null);

        getPerformancePlanChanges(targetMonth)
            .then((loadedChanges) => {
                if (monthRef.current !== targetMonth) {
                    return;
                }
                if (!Array.isArray(loadedChanges)) {
                    throw new Error("Не удалось загрузить историю изменений. Попробуйте еще раз.");
                }
                setChanges(loadedChanges);
                setLoadedHistoryMonth(targetMonth);
                setIsHistoryLoading(false);
            })
            .catch((error: unknown) => {
                if (monthRef.current !== targetMonth) {
                    return;
                }
                setHistoryError(errorMessage(error, "Не удалось загрузить историю изменений. Попробуйте еще раз."));
                setIsHistoryLoading(false);
            });
    }, []);

    const loadExecution = useCallback((targetMonth: string) => {
        setIsExecutionLoading(true);
        setExecutionError(null);

        getPerformancePlanExecution(targetMonth)
            .then((loadedExecution) => {
                if (monthRef.current !== targetMonth) {
                    return;
                }
                if (!loadedExecution) {
                    throw new Error("Не удалось загрузить выполнение планов. Попробуйте еще раз.");
                }
                setExecution(loadedExecution);
                setLoadedExecutionMonth(targetMonth);
                setIsExecutionLoading(false);
            })
            .catch((error: unknown) => {
                if (monthRef.current !== targetMonth) {
                    return;
                }
                setExecutionError(errorMessage(error, "Не удалось загрузить выполнение планов. Попробуйте еще раз."));
                setIsExecutionLoading(false);
            });
    }, []);

    useEffect(() => {
        if ((isSettings || isHistory) && loadedPlanMonth !== month) {
            loadPlan(month);
        }
    }, [isSettings, isHistory, loadedPlanMonth, month, loadPlan]);

    useEffect(() => {
        if (isHistory && loadedHistoryMonth !== month) {
            loadHistory(month);
        }
    }, [isHistory, loadedHistoryMonth, month, loadHistory]);

    useEffect(() => {
        if (isExecution && loadedExecutionMonth !== month) {
            loadExecution(month);
        }
    }, [isExecution, loadedExecutionMonth, month, loadExecution]);

    useEffect(() => {
        if (!plan) {
            setDraft(null);
            setInitialDraft(null);
            return;
        }
        const nextDraft = createDraft(month, plan, logistsWithoutPlan, carsWithoutPlan);
        setDraft(nextDraft);
        setInitialDraft(nextDraft);
    }, [month, plan, logistsWithoutPlan, carsWithoutPlan]);

    const isDirty = useMemo(
        () =>
            !!draft &&
            !!initialDraft &&
            (!!plan?.isCopiedFromPreviousMonth || JSON.stringify(draft) !== JSON.stringify(initialDraft)),
        [draft, initialDraft, plan?.isCopiedFromPreviousMonth],
    );

    const subjectNames = useMemo(() => {
        const names = new Map<string, string>();
        (execution?.logists || []).forEach((item) => {
            names.set(`Logist-${item.subjectId}`, item.name);
        });
        (execution?.cars || []).forEach((item) => {
            names.set(`Car-${item.subjectId}`, item.name);
        });
        (draft?.logistsPlans || plan?.logistsPlans || []).forEach((item) => {
            names.set(`Logist-${item.subjectId}`, item.displayName);
        });
        (draft?.carsPlans || plan?.carsPlans || []).forEach((item) => {
            names.set(`Car-${item.subjectId}`, item.displayName);
        });
        return names;
    }, [draft, plan, execution]);

    const resolveSubjectName = (change: PerformancePlanChangeLogModel) => {
        if (change.subjectType === "Organization") {
            return "Компания";
        }
        const typeName = change.subjectType === "Logist" ? "Логист" : "ТС";
        return `${typeName}: ${subjectNames.get(`${change.subjectType}-${change.subjectId}`) || `ID ${change.subjectId}`}`;
    };

    const changeMonth = (nextMonth: string) => {
        if (nextMonth !== month) {
            if (isDirty && !window.confirm("Имеются несохраненные изменения. Продолжить без сохранения?")) {
                return;
            }
            setMonth(nextMonth);
        }
    };

    const save = async () => {
        if (!draft || !plan) {
            return;
        }
        setIsSaving(true);
        setSaveError(null);

        try {
            const planToSave = preparePlanForSave(draft, plan);

            const organizationResult = await applyOrganizationPerformancePlan(planToSave);
            if (organizationResult === undefined || organizationResult === false) {
                setIsSaving(false);
                return;
            }

            const logistsResult = await applyLogistsPerformancePlan(planToSave);
            if (logistsResult === undefined || logistsResult === false) {
                setIsSaving(false);
                return;
            }

            const carsResult = await applyCarsPerformancePlan(planToSave);
            if (carsResult === undefined || carsResult === false) {
                setIsSaving(false);
                return;
            }

            setIsSaving(false);
            loadPlan(month);
        } catch (error: unknown) {
            setSaveError(errorMessage(error, "Не удалось сохранить план. Проверьте данные и попробуйте еще раз."));
            setIsSaving(false);
        }
    };

    const isCurrentTabLoading =
        isSettings ? isLoading : isHistory ? isHistoryLoading || isLoading : isExecutionLoading;

    const renderSettings = () => {
        if (isLoading) {
            return <Loader />;
        }
        if (loadError) {
            return (
                <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
                    <span>{loadError}</span>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger ml-3"
                        onClick={() => loadPlan(month)}
                    >
                        Повторить
                    </button>
                </div>
            );
        }
        if (!plan || !draft || !initialDraft) {
            return <div className="card card-body text-center text-muted">Данные плана не найдены.</div>;
        }

        return (
            <>
                {plan.isCopiedFromPreviousMonth && (
                    <div className="alert alert-info">
                        Показан план, перенесенный из предыдущего месяца. При сохранении для выбранного месяца будет
                        создана отдельная настройка.
                    </div>
                )}
                <div className="d-flex justify-content-end mb-3">
                    <div className="performance-plans__filter">
                        <PerformancePlanFilterSelect
                            inputProps={{ name: "performancePlanFilter" }}
                            options={filterOptions}
                            value={filter}
                            onChange={({ value }) => {
                                setFilter(value);
                            }}
                            unClearable
                            unSearchable
                        />
                    </div>
                </div>
                <PerformancePlanSettings
                    draft={draft}
                    initialDraft={initialDraft}
                    filter={filter}
                    isDirty={isDirty}
                    isSaving={isSaving}
                    saveError={saveError}
                    onChange={setDraft}
                    onSave={save}
                />
            </>
        );
    };

    const renderHistory = () => {
        if (isHistoryLoading) {
            return <Loader />;
        }
        if (historyError) {
            return (
                <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
                    <span>{historyError}</span>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger ml-3"
                        onClick={() => loadHistory(month)}
                    >
                        Повторить
                    </button>
                </div>
            );
        }
        return <PerformancePlanHistory changes={changes} resolveSubjectName={resolveSubjectName} />;
    };

    const renderExecution = () => {
        if (isExecutionLoading) {
            return <Loader />;
        }
        if (executionError) {
            return (
                <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
                    <span>{executionError}</span>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger ml-3"
                        onClick={() => loadExecution(month)}
                    >
                        Повторить
                    </button>
                </div>
            );
        }
        if (!execution) {
            return <div className="card card-body text-center text-muted">Нет данных о выполнении плана.</div>;
        }
        return <PerformancePlanExecution execution={execution} />;
    };

    return (
        <div className="page-content">
            <Transition dir="full">
                <div className="page-book__list book-list book-list--nofilter performance-plans">
                    <div className="book-list__head-wrap">
                        <div className="book-list__head">
                            <button
                                type="button"
                                aria-label="Закрыть раздел"
                                className="book-list__head-close close close--sm"
                                onClick={() => history.push(paths.root)}
                            />
                            <div className="d-flex align-items-center flex-wrap">
                                <div className="book-list__head-title mr-3">Планы по пробегу и выручке</div>
                                <MonthPicker
                                    month={month}
                                    disabled={isCurrentTabLoading || isSaving}
                                    onChange={changeMonth}
                                />
                            </div>
                            <div className="performance-plans__head-actions">
                                {!isExecution && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-secondary mr-2"
                                        onClick={() => history.push(paths.performancePlans)}
                                    >
                                        Выполнение планов
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className={cn(
                                        "btn btn-sm d-inline-flex align-items-center mr-2",
                                        isHistory ? "btn-brand" : "btn-secondary",
                                    )}
                                    onClick={() =>
                                        history.push(isHistory ? paths.performancePlans : paths.performancePlansHistory)
                                    }
                                >
                                    <IconHistory size={16} className="mr-1" />
                                    История
                                </button>
                                <button
                                    type="button"
                                    className={cn(
                                        "btn btn-sm d-inline-flex align-items-center",
                                        isSettings ? "btn-brand" : "btn-secondary",
                                    )}
                                    onClick={() =>
                                        history.push(
                                            isSettings ? paths.performancePlans : paths.performancePlansSettings,
                                        )
                                    }
                                >
                                    <IconSettings size={16} className="mr-1" />
                                    Настройки
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="list__body-wrap">
                        <div className="h-100 overflow-auto">
                            <div className="container-fluid pb-3">
                                <Switch>
                                    <Route exact path={paths.performancePlansSettings} render={renderSettings} />
                                    <Route exact path={paths.performancePlansHistory} render={renderHistory} />
                                    <Route path={paths.performancePlans} render={renderExecution} />
                                </Switch>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>
    );
}
